import { supabase } from "@/integrations/supabase/client";

export interface DetectionResult {
  centerX: number;
  centerY: number;
  pupilRadius: number;
  irisRadius: number;
  confidence: number;
  error?: string | null;
}

/**
 * Detect iris and pupil using the AI-powered edge function
 */
export async function detectIrisWithAI(
  imageData: string,
  eyeSide: "D" | "G",
  imageWidth: number,
  imageHeight: number
): Promise<DetectionResult> {
  try {
    const { data, error } = await supabase.functions.invoke("iris-detect", {
      body: {
        imageData,
        eyeSide,
        imageWidth,
        imageHeight,
      },
    });

    if (error) {
      console.error("Edge function error:", error);
      return getClientFallbackResult(imageWidth, imageHeight);
    }

    if (data?.error) {
      console.warn("Detection warning:", data.error);
    }

    return {
      centerX: data.centerX,
      centerY: data.centerY,
      pupilRadius: data.pupilRadius,
      irisRadius: data.irisRadius,
      confidence: data.confidence,
      error: data.error,
    };
  } catch (err) {
    console.error("Detection service error:", err);
    return getClientFallbackResult(imageWidth, imageHeight);
  }
}

/**
 * Client-side fallback detection using canvas analysis
 */
export async function detectIrisClientSide(imageData: string): Promise<DetectionResult> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        resolve(getClientFallbackResult(img.naturalWidth, img.naturalHeight));
        return;
      }

      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);

      const imageDataObj = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const result = analyzeImageForIris(imageDataObj, canvas.width, canvas.height);

      resolve(result);
    };

    img.onerror = () => {
      resolve(getClientFallbackResult(400, 400));
    };

    img.src = imageData;
  });
}

function getClientFallbackResult(width: number, height: number): DetectionResult {
  return {
    centerX: width / 2,
    centerY: height / 2,
    pupilRadius: Math.min(width, height) * 0.08,
    irisRadius: Math.min(width, height) * 0.3,
    confidence: 0.2,
    error: null,
  };
}

function analyzeImageForIris(
  imageData: ImageData,
  width: number,
  height: number
): DetectionResult {
  const data = imageData.data;

  // Convert to grayscale
  const grayscale: number[] = [];
  for (let i = 0; i < data.length; i += 4) {
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    grayscale.push(gray);
  }

  // Apply basic histogram equalization for better contrast
  const histogram = new Array(256).fill(0);
  for (const val of grayscale) {
    histogram[Math.floor(val)]++;
  }

  // Find adaptive dark threshold based on image histogram
  let totalPixels = grayscale.length;
  let cumulative = 0;
  let adaptiveThreshold = 60;
  
  for (let i = 0; i < 256; i++) {
    cumulative += histogram[i];
    if (cumulative > totalPixels * 0.05) {
      adaptiveThreshold = Math.min(i + 30, 100);
      break;
    }
  }

  // Find dark pixels (potential pupil)
  const darkPixels: { x: number; y: number; brightness: number }[] = [];

  // Focus on center region for better accuracy
  const marginX = width * 0.15;
  const marginY = height * 0.15;

  for (let y = Math.floor(marginY); y < height - marginY; y++) {
    for (let x = Math.floor(marginX); x < width - marginX; x++) {
      const idx = y * width + x;
      if (grayscale[idx] < adaptiveThreshold) {
        darkPixels.push({ x, y, brightness: grayscale[idx] });
      }
    }
  }

  if (darkPixels.length < 50) {
    return getClientFallbackResult(width, height);
  }

  // Calculate weighted centroid (darker pixels weighted more)
  let sumX = 0, sumY = 0, weightSum = 0;
  for (const pixel of darkPixels) {
    const weight = 1 / (pixel.brightness + 1);
    sumX += pixel.x * weight;
    sumY += pixel.y * weight;
    weightSum += weight;
  }

  const centerX = sumX / weightSum;
  const centerY = sumY / weightSum;

  // Estimate pupil radius from dark pixel cluster
  let maxDistFromCenter = 0;
  for (const pixel of darkPixels) {
    const dist = Math.sqrt((pixel.x - centerX) ** 2 + (pixel.y - centerY) ** 2);
    if (dist < Math.min(width, height) * 0.2) {
      maxDistFromCenter = Math.max(maxDistFromCenter, dist);
    }
  }
  const pupilRadius = Math.max(maxDistFromCenter * 0.85, Math.min(width, height) * 0.05);

  // Find iris radius by analyzing radial brightness gradient
  const irisRadius = findIrisRadiusImproved(grayscale, width, height, centerX, centerY, pupilRadius);

  // Calculate confidence
  const confidence = calculateImprovedConfidence(
    grayscale, width, height, centerX, centerY, pupilRadius, irisRadius
  );

  return {
    centerX,
    centerY,
    pupilRadius: Math.max(pupilRadius, 15),
    irisRadius: Math.max(irisRadius, pupilRadius + 30),
    confidence,
    error: null,
  };
}

function findIrisRadiusImproved(
  grayscale: number[],
  width: number,
  height: number,
  centerX: number,
  centerY: number,
  pupilRadius: number
): number {
  const numRays = 48;
  const radiusEstimates: number[] = [];
  const maxRadius = Math.min(width, height, centerX, width - centerX, centerY, height - centerY) - 5;

  for (let ray = 0; ray < numRays; ray++) {
    const angle = (ray / numRays) * 2 * Math.PI;

    // Sample brightness along this ray
    const samples: { r: number; brightness: number }[] = [];
    
    for (let r = pupilRadius + 5; r < maxRadius; r += 2) {
      const x = Math.round(centerX + Math.cos(angle) * r);
      const y = Math.round(centerY + Math.sin(angle) * r);

      if (x < 0 || x >= width || y < 0 || y >= height) break;

      samples.push({ r, brightness: grayscale[y * width + x] });
    }

    if (samples.length < 10) continue;

    // Find maximum gradient (iris to sclera transition)
    let maxGradient = 0;
    let maxGradientRadius = pupilRadius * 3;

    for (let i = 5; i < samples.length - 5; i++) {
      // Use smoothed gradient over multiple samples
      const beforeAvg = (samples[i-3].brightness + samples[i-2].brightness + samples[i-1].brightness) / 3;
      const afterAvg = (samples[i+1].brightness + samples[i+2].brightness + samples[i+3].brightness) / 3;
      const gradient = afterAvg - beforeAvg;

      if (gradient > maxGradient && samples[i].r > pupilRadius * 1.5) {
        maxGradient = gradient;
        maxGradientRadius = samples[i].r;
      }
    }

    if (maxGradient > 20) {
      radiusEstimates.push(maxGradientRadius);
    }
  }

  if (radiusEstimates.length < 8) {
    return pupilRadius * 3;
  }

  // Use median for robustness
  radiusEstimates.sort((a, b) => a - b);
  return radiusEstimates[Math.floor(radiusEstimates.length / 2)];
}

function calculateImprovedConfidence(
  grayscale: number[],
  width: number,
  height: number,
  centerX: number,
  centerY: number,
  pupilRadius: number,
  irisRadius: number
): number {
  // Check center position
  const centerInBounds =
    centerX > width * 0.2 && centerX < width * 0.8 &&
    centerY > height * 0.2 && centerY < height * 0.8;

  if (!centerInBounds) return 0.25;

  // Sample pupil region darkness
  let pupilDarkness = 0;
  let pupilSamples = 0;

  for (let angle = 0; angle < Math.PI * 2; angle += 0.3) {
    for (let r = 5; r < pupilRadius * 0.7; r += 3) {
      const x = Math.round(centerX + Math.cos(angle) * r);
      const y = Math.round(centerY + Math.sin(angle) * r);

      if (x >= 0 && x < width && y >= 0 && y < height) {
        pupilDarkness += grayscale[y * width + x];
        pupilSamples++;
      }
    }
  }

  const avgPupilDarkness = pupilSamples > 0 ? pupilDarkness / pupilSamples : 255;

  // Sample iris region brightness
  let irisBrightness = 0;
  let irisSamples = 0;

  for (let angle = 0; angle < Math.PI * 2; angle += 0.3) {
    const r = (pupilRadius + irisRadius) / 2;
    const x = Math.round(centerX + Math.cos(angle) * r);
    const y = Math.round(centerY + Math.sin(angle) * r);

    if (x >= 0 && x < width && y >= 0 && y < height) {
      irisBrightness += grayscale[y * width + x];
      irisSamples++;
    }
  }

  const avgIrisBrightness = irisSamples > 0 ? irisBrightness / irisSamples : 0;

  // Calculate confidence based on contrast
  let confidence = 0.4;

  if (avgPupilDarkness < 80) confidence += 0.15;
  if (avgIrisBrightness > 50 && avgIrisBrightness < 200) confidence += 0.15;
  if (avgIrisBrightness - avgPupilDarkness > 25) confidence += 0.2;
  if (irisRadius > pupilRadius * 2 && irisRadius < pupilRadius * 5) confidence += 0.1;

  return Math.min(confidence, 1);
}
